import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useLoggedInUser } from '../../@biz_account/useLoggedInUser/useLoggedInUser'
import { useAlert } from '~/@ui/alert/useAlert'
import { phoneScheme, PhoneScheme } from './_scheme/phone.scheme'
import { usePhoneLoginVerification } from './usePhoneLoginVerification'
import { CodeInput } from '~/@ui/phone/CodeInput'
import { PhoneInput } from '~/@ui/phone/PhoneInput'

export const PhoneLoginPage = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { control, getValues, trigger } = useForm<PhoneScheme>({
    resolver: zodResolver(phoneScheme),
    mode: 'onBlur',
    defaultValues: {
      phonePrefix: '+421',
      phoneNumber: '',
    },
  })

  const redirectAfterLogin = async () => {
    const response = await checkLoggedInUser()

    if (response?.user.isInvestor) {
      navigate('/investor/portfolio')
      return
    }

    if (response?.user.isOwner) {
      navigate('/vlastnik/portfolio')
      return
    }

    showAlert('error', 'Nastala neočakávaná chyba. Kontaktujte podporu.')
  }

  const {
    isVerifyingPhone,
    startVerification,
    stopVerification,

    verificationCode,
    verifyCodeInputState,

    handleCodeChange,
    handleSendAgainClick,

    waiting,
    secondsLeft,

    isStartLoading,
    isVerifyLoading,
  } = usePhoneLoginVerification({
    onSuccess: redirectAfterLogin,
  })

  const state = waiting || isStartLoading || isVerifyLoading
  const verifyCodeInputClassName = `i-verifycodeinput-${verifyCodeInputState}`

  const handleSubmitClick = async () => {
    const isValid = await trigger()

    if (!isValid) {
      showAlert('error', 'Prosím, vyplňte tel. č. správne.')
      return
    }

    const phone = `${getValues('phonePrefix')} ${getValues('phoneNumber')}`

    startVerification(phone)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmitClick()
    }
  }

  const { hasAuthToken, checkLoggedInUser } = useLoggedInUser()

  useEffect(() => {
    const loadUserOrNavigateToLogin = async () => {
      if (hasAuthToken()) {
        const userResponse = await checkLoggedInUser()

        if (userResponse && 'success' in userResponse && userResponse.success) {
          if (userResponse.user.isInvestor) {
            navigate('/investor/portfolio')
          } else {
            navigate('/vlastnik/portfolio')
          }
        }
      }
    }

    loadUserOrNavigateToLogin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return !isStartLoading && isVerifyingPhone ? (
    <CodeInput
      title="Zadajte prihlasovací PIN"
      stopVerification={stopVerification}
      getValues={getValues}
      verificationCode={verificationCode}
      handleCodeChange={handleCodeChange}
      verifyCodeInputClassName={verifyCodeInputClassName}
      state={state}
      secondsLeft={secondsLeft}
      handleSendAgainClick={handleSendAgainClick}
    />
  ) : (
    <PhoneInput
      title="Vitajte späť!"
      text="Prihláste sa do svojho účtu Bonaship."
      onClickBack={() => (window.location.href = 'https://bonaship.sk')}
      continueButtonLabel="Pokračovať"
      questionText="Ešte nemáte u nás účet?"
      linkLabel="Vytvoriť účet"
      link="/account/register"
      control={control}
      handleKeyDown={handleKeyDown}
      handleSubmitClick={handleSubmitClick}
      isStartLoading={isStartLoading}
    />
  )
}
